import { useEffect, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { useNavigate, useParams } from "react-router";
import { Ban, Trash2 } from "lucide-react";

import { ApiError } from "@/api/client";
import {
  cancelCampaign,
  createCampaign,
  deleteCampaign,
  getCampaign,
  listCampaignRecipients,
  listTemplates,
  resolveCampaign,
  sendCampaign,
  updateCampaign,
} from "@/api/messaging";
import { ConfirmModal } from "@/components/ConfirmModal";
import { KebabMenu, MenuItem } from "@/components/KebabMenu";
import { AudienceBuilder } from "@/components/messaging/AudienceBuilder";
import { CampaignStatusBadge } from "@/components/messaging/badges";
import { Field, Input, Select } from "@/components/form";
import { ErrorBox, MutationError, RefreshFailed, Spinner, loadFailure } from "@/components/Spinner";
import { formatDateTime } from "@/lib/dates";
import type {
  MessagingAudienceSelector,
  MessagingCampaignCounts,
  MessagingCampaignInput,
  MessagingRecipient,
} from "@/types/api";
import { Button, EmptyState, FormActionBar, Notice, Page, PageHeader, StatusPill, Table, type TableColumn } from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

const SOURCE_LABELS: Record<string, string> = {
  tenant_users: "Tenant users",
  tenant_admins: "Tenant admins",
  operators: "Operators",
};

const RECIPIENT_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
  { value: "skipped", label: "Skipped" },
];

const RECIPIENT_COLUMNS: TableColumn<MessagingRecipient>[] = [
  { key: "email", header: "Email", cell: (r) => r.email },
  {
    key: "tenant",
    header: "Tenant / stage",
    cell: (r) => (
      <>
        {r.tenantId ?? "—"}
        {r.stage ? <span className="text-ink-muted"> / {r.stage}</span> : null}
        {r.isProduction && (
          <StatusPill tone="danger" dot={false} className="ml-2">
            prod
          </StatusPill>
        )}
      </>
    ),
  },
  { key: "source", header: "Source", cell: (r) => SOURCE_LABELS[r.source] ?? r.source },
  {
    key: "status",
    header: "Status",
    cell: (r) => (
      <>
        {r.status}
        {r.error && <span className="ml-2 text-xs text-danger-fg">{r.error}</span>}
      </>
    ),
  },
];

// The recipients count matching the active status filter — "All" maps to
// the total resolved, any other filter maps to its own key of the campaign's
// counts (which always carries all six keys).
function countForFilter(counts: MessagingCampaignCounts, status: string): number {
  switch (status) {
    case "pending":
      return counts.pending;
    case "sent":
      return counts.sent;
    case "failed":
      return counts.failed;
    case "skipped":
      return counts.skipped;
    default:
      return counts.resolved;
  }
}

// The send route returns 403 when the production key is missing or wrong,
// and 409 when the campaign moved on, the resolved audience is empty or
// stale, or the template changed since it was resolved — all expected,
// recoverable states that deserve a specific sentence instead of the raw
// backend error, same pattern as the 412/409 handling in
// MessagingTemplateEdit.tsx. The 409 body always carries a message tailored
// to the reason (see messaging_api/handler.py `_send_campaign`); show it
// verbatim and fall back to a generic sentence only when it is missing.
function sendErrorMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 403) return "The production key is missing or incorrect.";
  if (error.status === 409) {
    const body = error.body;
    if (typeof body === "object" && body !== null && "message" in body && typeof (body as { message: unknown }).message === "string") {
      return (body as { message: string }).message;
    }
    return "The campaign changed, or the resolved audience is more than 24 hours old. Resolve the audience again, then send.";
  }
  return null;
}

export function MessagingCampaignEditPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const { campaignId } = useParams();
  const isNew = !campaignId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<MessagingCampaignInput>({ name: "", templateSlug: "", audience: [] });
  // The stored campaign is put into the form ONCE, when it first arrives (and again when the address
  // names another campaign): the poll of a campaign that is sending, and the refetch after an action,
  // must not overwrite what the operator is typing.
  const [primed, setPrimed] = useState(false);
  // A form that creates a campaign is open from the first render; one that edits it when it is
  // primed. Dirty = what the form shows differs from what it was opened (or last saved) with, and the
  // way out of the page is held while it is.
  const { dirty, reset: markSaved } = useDirtyValues(form, isNew || primed);
  const guard = useUnsavedChanges(dirty);
  const [showSend, setShowSend] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [recipientStatus, setRecipientStatus] = useState("");

  const campaign = useQuery({
    queryKey: ["messaging", "campaign", campaignId],
    queryFn: () => getCampaign(token, campaignId ?? ""),
    enabled: !!token && !isNew,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "resolving" || status === "sending" ? 5_000 : false;
    },
  });
  const templates = useQuery({ queryKey: ["messaging", "templates"], queryFn: () => listTemplates(token), enabled: !!token });
  const recipientsEnabled =
    !!token && !isNew && !!campaign.data && campaign.data.status !== "draft" && campaign.data.status !== "resolving";
  const recipients = useInfiniteQuery({
    queryKey: ["messaging", "campaign", campaignId, "recipients", recipientStatus],
    queryFn: ({ pageParam }) =>
      listCampaignRecipients(token, campaignId ?? "", { status: recipientStatus || undefined, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: recipientsEnabled,
    // A status-filter change swaps the query key — without this the table
    // would unmount into a spinner on every click of the filter.
    placeholderData: keepPreviousData,
  });
  const recipientItems = recipients.data?.pages.flatMap((p) => p.items) ?? [];

  useEffect(() => {
    setPrimed(false);
  }, [campaignId]);
  useEffect(() => {
    if (!campaign.data || primed) return;
    setForm({ name: campaign.data.name, templateSlug: campaign.data.templateSlug, audience: campaign.data.audience });
    setPrimed(true);
  }, [campaign.data, primed]);

  // Keep the recipients window current while the campaign is sending — tied
  // to the campaign query's own poll tick (not a second independent timer),
  // and a refetch of already-loaded pages rather than a key change, so the
  // table never flickers into a spinner.
  useEffect(() => {
    if (recipientsEnabled && campaign.data?.status === "sending") recipients.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.dataUpdatedAt]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["messaging", "campaigns"] });
    queryClient.invalidateQueries({ queryKey: ["messaging", "campaign", campaignId] });
  };

  const save = useMutation({
    mutationFn: () => (isNew ? createCampaign(token, form) : updateCampaign(token, campaignId ?? "", form)),
    onSuccess: (saved) => {
      invalidate();
      if (isNew) {
        markSaved();
        guard.leave(`/messaging/campaigns/${saved.id}`, { replace: true });
      } else {
        // Stays on the page: the form now holds what the server holds.
        setForm({ name: saved.name, templateSlug: saved.templateSlug, audience: saved.audience });
        markSaved();
      }
    },
  });
  const resolve = useMutation({ mutationFn: () => resolveCampaign(token, campaignId ?? ""), onSuccess: invalidate });
  const send = useMutation({
    mutationFn: (productionKey?: string) => sendCampaign(token, campaignId ?? "", productionKey),
    onSuccess: () => {
      setShowSend(false);
      invalidate();
    },
  });
  const cancel = useMutation({
    mutationFn: () => cancelCampaign(token, campaignId ?? ""),
    onSuccess: () => {
      setShowCancel(false);
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteCampaign(token, campaignId ?? ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messaging", "campaigns"] });
      // Deleting the campaign is a deliberate end to what the form held.
      guard.leave("/messaging/campaigns", { replace: true });
    },
  });

  const status = campaign.data?.status;
  const editable = isNew || status === "draft" || status === "ready";
  const busy = status === "resolving" || status === "sending";
  const c = campaign.data;
  const sendSpecificError = sendErrorMessage(send.error);
  // The two destructive gestures of a campaign live in its menu, and open the
  // confirmation each already has.
  const canCancel =
    status === "draft" || status === "ready" || (status === "sending" && !c?.cancelRequested);
  const canDelete =
    status === "draft" || status === "ready" || status === "cancelled" || status === "failed";

  return (
    <Page>
      <PageHeader
        back={{ to: "/messaging/campaigns", label: "Campaigns" }}
        // The campaign's name; while it loads the title is a bar, and if it cannot load the page
        // still says what it is.
        title={isNew ? "New campaign" : c ? c.name : campaign.isError ? "Campaign" : undefined}
        status={
          c && (
            <div className="flex items-center gap-2">
              <CampaignStatusBadge status={c.status} />
              {c.requiresProductionKey && (
                <StatusPill
                  tone="danger"
                  dot={false}
                  title="At least one recipient belongs to a tenant with a production stage: the production key is required to send."
                >
                  Production
                </StatusPill>
              )}
            </div>
          )
        }
        actions={
          c && (
            <>
              {(status === "draft" || status === "ready") && (
                <Button
                  variant="secondary"
                  loading={resolve.isPending}
                  disabled={dirty}
                  title={dirty ? "Save the campaign first: the audience is resolved from the saved version." : undefined}
                  onClick={() => resolve.mutate()}
                >
                  {resolve.isPending ? "Starting…" : status === "ready" ? "Resolve again" : "Resolve audience"}
                </Button>
              )}
              {status === "ready" && (
                <>
                  <Button
                    variant="primary"
                    disabled={c.counts.resolved === 0 || dirty}
                    title={dirty ? "Save the campaign first: the saved version is what is sent." : undefined}
                    onClick={() => setShowSend(true)}
                  >
                    Send
                  </Button>
                  {c.counts.resolved === 0 && (
                    <span className="text-xs text-warning-fg">No recipient resolved. Adjust the audience and resolve again.</span>
                  )}
                </>
              )}
              {(canCancel || canDelete) && (
                <KebabMenu label="Campaign actions">
                  {canCancel && (
                    <MenuItem danger icon={Ban} onClick={() => setShowCancel(true)}>
                      Cancel campaign…
                    </MenuItem>
                  )}
                  {canDelete && (
                    <MenuItem danger icon={Trash2} onClick={() => setShowDelete(true)}>
                      Delete…
                    </MenuItem>
                  )}
                </KebabMenu>
              )}
            </>
          )
        }
      />

      {campaign.isLoading && <Spinner />}
      {campaign.isError && <ErrorBox error={campaign.error} />}
      <MutationError mutation={resolve} title="Could not resolve the audience" className="mb-4" />
      {c?.error && (
        <Notice tone="danger" className="mb-4">
          {c.error}
        </Notice>
      )}
      {busy && (
        <Notice tone="info" className="mb-4">
          {status === "resolving"
            ? "Resolving the audience…"
            : `Sending… ${c?.counts.sent ?? 0} sent, ${c?.counts.failed ?? 0} failed, ${c?.counts.skipped ?? 0} skipped, ${c?.counts.pending ?? 0} pending`}
          {c?.cancelRequested && " — cancellation requested, remaining recipients will be skipped."}
        </Notice>
      )}

      {(isNew || c) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="card space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name">
                <Input value={form.name} disabled={!editable} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
              </Field>
              <div>
                <Field label="Template">
                  <Select
                    value={form.templateSlug}
                    disabled={!editable}
                    required
                    placeholder="Choose a template"
                    onChange={(e) => setForm((f) => ({ ...f, templateSlug: e.target.value }))}
                    options={(templates.data?.items ?? []).map((t) => ({ value: t.slug, label: `${t.name} (${t.category})` }))}
                  />
                </Field>
                {templates.isError && <ErrorBox error={templates.error} />}
              </div>
            </div>
            <div>
              <div className="mb-1 text-sm text-ink-muted">Audience</div>
              <AudienceBuilder value={form.audience} disabled={!editable} token={token} onChange={(audience: MessagingAudienceSelector[]) => setForm((f) => ({ ...f, audience }))} />
            </div>
            {editable && status === "ready" && (
              <p className="text-xs text-warning-fg">Saving returns the campaign to draft: resolve the audience again before sending.</p>
            )}
          </div>

          {editable && (
            <FormActionBar
              dirty={dirty}
              submitLabel={isNew ? "Create campaign" : "Save"}
              pending={save.isPending}
              disabled={form.audience.length === 0}
              disabledReason="Add at least one audience selector."
              onCancel={() => navigate("/messaging/campaigns")}
              error={<MutationError mutation={save} title="Could not save the campaign" />}
            />
          )}
        </form>
      )}

      {c && c.status !== "draft" && c.status !== "resolving" && (
        // No card: the recipients table draws its own frame (a frame inside a frame is two greys).
        <div className="mt-6">
          <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-ink-muted">
            <h2 className="text-lg font-medium text-ink">Recipients</h2>
            <span>
              <strong>{c.counts.resolved}</strong> resolved
            </span>
            {c.perSource.map((s, i) => (
              <span key={i}>
                {SOURCE_LABELS[s.kind] ?? s.kind}: <strong>{s.count}</strong>
              </span>
            ))}
            {c.counts.duplicates > 0 && <span>{c.counts.duplicates} duplicates removed</span>}
            {c.resolvedAt && <span className="text-ink-muted">resolved {formatDateTime(c.resolvedAt)}</span>}
          </div>
          {c.warnings.length > 0 && (
            <ul className="mb-3 list-inside list-disc text-sm text-warning-fg">
              {c.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          <div className="mb-3">
            <Field label="Status">
              <Select
                value={recipientStatus}
                onChange={(e) => setRecipientStatus(e.target.value)}
                options={[{ value: "", label: "All" }, ...RECIPIENT_STATUS_OPTIONS]}
              />
            </Field>
          </div>
          <RefreshFailed query={recipients} className="mb-3" />
          <Table
            label="Recipients"
            columns={RECIPIENT_COLUMNS}
            rows={recipients.data ? recipientItems : undefined}
            rowKey={(r) => r.email}
            error={loadFailure(recipients)}
            more={
              recipients.hasNextPage
                ? { loading: recipients.isFetchingNextPage, onLoadMore: () => void recipients.fetchNextPage() }
                : undefined
            }
            empty={<EmptyState title={recipientStatus ? "No recipients match this filter" : "No recipients"} />}
          />
          {recipients.data && (
            <p className="mt-2 text-xs text-ink-muted">
              Showing {recipientItems.length} of {countForFilter(c.counts, recipientStatus)} recipients.
            </p>
          )}
        </div>
      )}

      {showSend && c && (
        <ConfirmModal
          title={`Send "${c.name}"`}
          confirmLabel={`Send to ${c.counts.resolved} recipient${c.counts.resolved === 1 ? "" : "s"}`}
          pendingLabel="Starting…"
          pending={send.isPending}
          danger={c.requiresProductionKey}
          productionKey={c.requiresProductionKey}
          error={sendSpecificError ? undefined : send.error}
          onCancel={() => {
            setShowSend(false);
            send.reset();
          }}
          onConfirm={(key) => send.mutate(key)}
        >
          <p>
            The template "{c.templateSlug}" will be sent to the {c.counts.resolved} recipients resolved
            {c.resolvedAt ? ` on ${formatDateTime(c.resolvedAt)}` : ""}. This cannot be undone once the
            first email leaves.
          </p>
          {c.requiresProductionKey && (
            <p className="mt-2">
              At least one recipient belongs to a tenant with a production stage: paste the production key
              from the password manager.
            </p>
          )}
          {sendSpecificError && (
            // A refusal that appears after the click: announced without moving focus.
            <Notice tone="danger" role="alert" className="mt-2">
              {sendSpecificError}
            </Notice>
          )}
        </ConfirmModal>
      )}

      {showDelete && c && (
        <ConfirmModal
          title={`Delete "${c.name}"`}
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          pending={remove.isPending}
          danger
          error={remove.error}
          onCancel={() => {
            remove.reset();
            setShowDelete(false);
          }}
          onConfirm={() => remove.mutate()}
        >
          <p>The campaign and its resolved recipients will be removed. Sent campaigns cannot be deleted.</p>
        </ConfirmModal>
      )}

      {showCancel && c && (
        <ConfirmModal
          title="Cancel this campaign?"
          confirmLabel="Cancel campaign"
          pendingLabel="Cancelling…"
          pending={cancel.isPending}
          danger
          error={cancel.error}
          onCancel={() => {
            cancel.reset();
            setShowCancel(false);
          }}
          onConfirm={() => cancel.mutate()}
        >
          {status === "sending" ? (
            <p>
              The campaign is already sending. Cancelling stops it from sending to anyone else: the
              recipients it already reached keep their email, the remaining ones are marked skipped, and
              the campaign ends as sent.
            </p>
          ) : (
            <p>The campaign will be marked cancelled. A cancelled campaign cannot be sent — only deleted.</p>
          )}
        </ConfirmModal>
      )}
    </Page>
  );
}
